import { useEffect, useState } from 'react';
import { useSymbol } from '@/lib/useSymbol';
import { useQuote } from '@/features/alerts/api';

/** Older than this and the feed is not live — the exporter writes once a second. */
export const FEED_STALE_AFTER_SEC = 120;

export type FeedState =
  /** Pending first answer: say nothing rather than flash an alarm on load. */
  | 'loading'
  | 'live'
  /** The terminal says it has lost the broker. */
  | 'disconnected'
  /** Nothing written for longer than FEED_STALE_AFTER_SEC. */
  | 'stale'
  /** The API could not give a price at all. */
  | 'unreachable';

export interface FeedHealth {
  state: FeedState;
  /** Real unix seconds of the last write, when known. */
  updatedAt: number | null;
  /** Seconds since that write, ticking. */
  age: number | null;
}

/**
 * Whether MT5 data is live, for the selected pair.
 *
 * One definition, shared by the dashboard badge and the app-wide banner, so the
 * two can never disagree about whether the feed is down. Built on the alerts
 * quote, which is already polled every couple of seconds.
 */
export function useFeedHealth(): FeedHealth {
  const { symbol } = useSymbol();
  const quote = useQuote(symbol);

  const [now, setNow] = useState(() => Date.now() / 1000);
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now() / 1000), 1000);
    return () => clearInterval(id);
  }, []);

  const updatedAt = quote.data?.updatedAt ?? null;
  const age = updatedAt === null ? null : Math.max(0, now - updatedAt);

  let state: FeedState;
  if (!symbol || (quote.isPending && !quote.isError)) state = 'loading';
  else if (quote.isError && !quote.data) state = 'unreachable';
  else if (quote.data?.connected === false) state = 'disconnected';
  else if (age === null || age > FEED_STALE_AFTER_SEC) state = 'stale';
  else state = 'live';

  return { state, updatedAt, age };
}

/** Local wall clock, with the date only when it is not today. */
export function formatFeedClock(sec: number): string {
  const d = new Date(sec * 1000);
  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  return d.toDateString() === new Date().toDateString()
    ? time
    : `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${time}`;
}
